import type { AccessDescriptor, CommercialUpgradeRoute } from '../models/AccessDescriptor';
import type { PlanType } from '../models/Entitlements';
import type { ProductId } from '../models/ProductGrant';

export interface CommercialUpgradePresentation {
  readonly targetPlan: 'pro' | 'business' | 'enterprise';
  readonly eyebrow: string;
  readonly title: string;
  readonly description: string;
  readonly outcomeTitle: string;
  readonly outcomeDescription: string;
  readonly benefits: readonly string[];
  readonly price: string;
  readonly priceDetail: string;
  readonly ctaLabel: string;
  readonly href: string;
}

interface CommercialUpgradePresentationInput {
  readonly currentPlan?: PlanType;
  readonly feature?: string;
  readonly reason?: string;
  readonly descriptor?: AccessDescriptor;
}

const PRODUCT_LABELS: Record<ProductId, string> = {
  atlas: 'Atlas',
  process: 'Process',
  blueprint: 'Blueprint',
  compass: 'Compass',
  clinical: 'Genogram',
};

const PRODUCT_OUTCOMES: Record<ProductId, Pick<CommercialUpgradePresentation, 'outcomeTitle' | 'outcomeDescription' | 'benefits'>> = {
  atlas: {
    outcomeTitle: 'See the roles that need a succession plan first',
    outcomeDescription: 'Turn a structural org chart into governed planning signals, scenarios, and decisions.',
    benefits: ['Succession and workforce planning signals', 'Governed scenarios and time travel', 'Product-scoped evaluation with your team'],
  },
  process: {
    outcomeTitle: 'Move from a procedure to a review-ready control trail',
    outcomeDescription: 'Connect extracted work to its evidence, ownership, and review history.',
    benefits: ['Evidence-backed extraction', 'RACI and provenance views', 'Product-scoped evaluation with your team'],
  },
  blueprint: {
    outcomeTitle: 'Find the exposure path worth fixing first',
    outcomeDescription: 'Connect priority findings to affected systems, owners, and scheduled follow-up.',
    benefits: ['Exposure paths and drift', 'Prioritized finding queues', 'Product-scoped evaluation with your team'],
  },
  compass: {
    outcomeTitle: 'Turn readiness into the next defensible action',
    outcomeDescription: 'Move from a high-level score into governed evidence, reviews, and audit decisions.',
    benefits: ['Evidence and audit workflows', 'Reviewer decisions and drift', 'Product-scoped evaluation with your team'],
  },
  clinical: {
    outcomeTitle: 'Bring a family pattern into focus safely',
    outcomeDescription: 'Use governed pattern detection and reporting inside the clinical review workflow.',
    benefits: ['Pattern detection and field packs', 'Governed clinical reports', 'Product-scoped evaluation with your team'],
  },
};

const ROUTE_TARGET: Record<CommercialUpgradeRoute, CommercialUpgradePresentation['targetPlan']> = {
  pro_checkout: 'pro',
  business_checkout: 'business',
  enterprise_sales: 'enterprise',
};

export function getCommercialUpgradePresentation({
  currentPlan = 'free',
  feature,
  reason,
  descriptor,
}: CommercialUpgradePresentationInput): CommercialUpgradePresentation {
  const targetPlan = descriptor?.upgradeRoute
    ? ROUTE_TARGET[descriptor.upgradeRoute]
    : nextPlan(currentPlan, feature);
  const product = descriptor?.requiredProduct;
  const query = new URLSearchParams({
    source: 'native_upgrade_dialog',
    capability: descriptor?.capability ?? feature ?? 'generic',
  });
  if (product) query.set('product', product);

  if (targetPlan === 'enterprise') {
    const productLabel = product ? PRODUCT_LABELS[product] : 'Advanced';
    const outcome = product ? PRODUCT_OUTCOMES[product] : {
      outcomeTitle: 'Shape an Enterprise workflow around your outcome',
      outcomeDescription: 'Bring advanced product depth, governance, and evaluation into one scoped rollout.',
      benefits: ['Advanced product capabilities', 'A scoped evaluation', 'Sales-quoted deployment'],
    };
    return {
      targetPlan,
      eyebrow: `${productLabel} · Enterprise`,
      title: `Take ${productLabel} from a useful view to a working decision`,
      description: reason ?? `${productLabel} Advanced is available through a scoped Enterprise grant or evaluation.`,
      ...outcome,
      price: 'Sales quoted',
      priceDetail: 'Scoped to the product and rollout you need',
      ctaLabel: 'Contact sales',
      href: `https://creately.com/contact-sales/?${query.toString()}`,
    };
  }

  if (targetPlan === 'business') {
    query.set('plan', 'business');
    query.set('app', '1');
    return {
      targetPlan,
      eyebrow: 'Business',
      title: 'Connect every source your organization needs',
      description: reason ?? 'Business removes the active-source ceiling while keeping the experience focused on drawing and data sync.',
      outcomeTitle: 'Keep every operating view current',
      outcomeDescription: 'Move from one live source to unlimited active sources and keep the team working from the same picture.',
      benefits: ['Unlimited active synced sources', 'Basic drawing across every product', '10 lifetime assisted outcomes'],
      price: 'US$1,000 / year',
      priceDetail: 'Per organization',
      ctaLabel: 'Upgrade to Business',
      href: `${import.meta.env.VITE_SITE_URL}/plans/?${query.toString()}`,
    };
  }

  query.set('plan', 'pro');
  query.set('app', '1');
  const shapeLimit = feature === 'shapes.unlimited' || feature === 'items-limit' || feature?.startsWith('shapes.');
  const task = feature === 'create-folder' ? {
    title: 'Create another folder with Pro',
    outcomeTitle: 'Give this work its own folder',
    outcomeDescription: 'Organize related diagrams in additional folders, then continue creating where you left off.',
    benefits: ['Additional diagram folders', 'Unlimited basic drawing'],
  } : feature === 'export.branding' ? {
    title: 'Export without Creately branding',
    outcomeTitle: 'Share a clean copy of your work',
    outcomeDescription: 'Remove Creately branding from your exports for presentations and delivery to your team or clients.',
    benefits: ['Exports without Creately branding', 'Unlimited basic drawing'],
  } : null;
  return {
    targetPlan,
    eyebrow: 'Pro',
    title: task?.title ?? (shapeLimit ? 'Give the whole idea room to grow' : 'Keep this work moving with Pro'),
    description: reason ?? task?.outcomeDescription ?? 'Pro unlocks unlimited basic drawing and a live source so you can experience the connected workflow.',
    outcomeTitle: task?.outcomeTitle ?? (shapeLimit ? 'Continue this canvas without a shape ceiling' : 'Turn one source into a living workspace'),
    outcomeDescription: task?.outcomeDescription ?? (shapeLimit
      ? 'Keep every person, system, and connection together on the canvas you already started.'
      : 'Connect one active source, refresh it daily, and see how a synced workspace behaves before scaling up.'),
    benefits: task?.benefits ?? ['Unlimited basic drawing', '1 active synced source', '5 lifetime assisted outcomes'],
    price: 'US$8 / month',
    priceDetail: 'Or US$60 billed yearly',
    ctaLabel: 'Upgrade to Pro',
    href: `${import.meta.env.VITE_SITE_URL}/plans/?${query.toString()}`,
  };
}

function nextPlan(currentPlan: PlanType, feature?: string): CommercialUpgradePresentation['targetPlan'] {
  if (feature?.startsWith('commercial.enterprise.')) return 'enterprise';
  if (currentPlan === 'pro') return 'business';
  if (currentPlan === 'business' || currentPlan === 'paid' || currentPlan === 'enterprise') return 'enterprise';
  return 'pro';
}
